/**
 * Board pack (Dashboard 2) — bird’s-eye lamps + categorized charts, then line lists.
 * Live figures from /api/governance/board-dashboard. Empty sources stay at zero.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bar, BarChart, CartesianGrid, Cell, Label, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useGovernanceStats, cddTierCount, riskOpenCount, ttrOpenCount } from '../../hooks/useGovernanceStats'
import { captureSlides, exportSlidesToPdf, exportSlidesToPptx } from '../../utils/exportElementPdf'
import { buildFormattedBoardReportData } from '../../utils/boardPackFormattedReportData'
import { exportFormattedBoardReport } from '../../utils/exportFormattedBoardReport'

import { formatSydneyDate } from '../../utils/sydneyDateTime'
const F = { sans: "'Poppins', system-ui, sans-serif" }
const C = {
  pri: '#0a5169', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', amber: '#d97706', green: '#16a34a',
  orange: '#ea580c', purple: '#7c3aed',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
  ink: '#0f172a', border: '#e2e8f0',
}

const RAG_COPY = {
  green: 'OK',
  amber: 'Working on it',
  red: 'Ask questions',
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    return formatSydneyDate(new Date(iso))
  } catch {
    return '—'
  }
}

function ragColor(rag) {
  return rag === 'red' ? C.red : rag === 'amber' ? C.amber : C.green
}

const AXIS = { fontSize: 11, fill: C.slate }
const TIP = { borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, boxShadow: 'none' }
const LEG = { fontSize: 11, color: C.slate }

function ChartGrid() {
  return <CartesianGrid strokeDasharray="3 3" stroke="#edf2f5" vertical={false} />
}

function packRagOf(rags) {
  if (rags.includes('red')) return 'red'
  if (rags.includes('amber')) return 'amber'
  return 'green'
}

function residualBand(score) {
  if (score >= 70) return 'High'
  if (score >= 40) return 'Medium'
  return 'Low'
}

function Donut({ value, color, size = 72, suffix = '%' }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0))
  const r = 26
  const circ = 2 * Math.PI * r
  const dash = (pct / 100) * circ
  const label = suffix === '%' ? `${Math.round(pct)}%` : String(value)
  return (
    <svg width={size} height={size} viewBox="0 0 68 68" aria-hidden>
      <circle cx="34" cy="34" r={r} fill="none" stroke="#e8eef2" strokeWidth="7" />
      <circle
        cx="34" cy="34" r={r} fill="none" stroke={color} strokeWidth="7"
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
        transform="rotate(-90 34 34)"
      />
      <text x="34" y="38" textAnchor="middle" fontSize="13" fontWeight="800" fill={color} fontFamily={F.sans}>
        {label}
      </text>
    </svg>
  )
}

function Rag({ rag }) {
  const color = ragColor(rag)
  const bg = rag === 'red' ? '#fef2f2' : rag === 'amber' ? '#fffbeb' : '#f0fdf4'
  const bd = rag === 'red' ? '#fecaca' : rag === 'amber' ? '#fde68a' : '#bbf7d0'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700,
      padding: '3px 9px', borderRadius: 20, color, background: bg, border: `1px solid ${bd}`,
      whiteSpace: 'nowrap',
    }}>
      {RAG_COPY[rag]}
    </span>
  )
}

function EmptyHint({ children }) {
  return <div style={{ fontSize: 12, color: C.slateL, padding: '28px 8px', textAlign: 'center' }}>{children}</div>
}

function ChartBox({ height = 200, children, empty }) {
  if (empty) return <div className="chart-box" style={{ height }}><EmptyHint>No data this period</EmptyHint></div>
  return <div className="chart-box" style={{ height }}>{children}</div>
}

function PieCentre({ viewBox, value, label }) {
  const cx = viewBox?.cx ?? 0
  const cy = viewBox?.cy ?? 0
  return (
    <text textAnchor="middle" fontFamily={F.sans}>
      <tspan x={cx} y={label ? cy - 4 : cy + 5} fontSize="20" fontWeight="800" fill={C.pri}>{value}</tspan>
      {label ? (
        <tspan x={cx} y={cy + 14} fontSize="9" fontWeight="700" fill={C.slate}>{String(label).toUpperCase()}</tspan>
      ) : null}
    </text>
  )
}

function CentrePie({ data, height = 220, centerValue, centerLabel }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="44%"
          innerRadius="46%"
          outerRadius="74%"
          paddingAngle={2}
        >
          {data.map((b, i) => <Cell key={b.name || i} fill={b.color || C.pri} />)}
          {centerValue != null && centerValue !== '' && (
            <Label position="center" content={(props) => (
              <PieCentre viewBox={props.viewBox} value={centerValue} label={centerLabel} />
            )} />
          )}
        </Pie>
        <Tooltip contentStyle={TIP} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={LEG} />
      </PieChart>
    </ResponsiveContainer>
  )
}

function Panel({ title, hint, children, id }) {
  return (
    <div id={id} className="panel" style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '16px 16px 14px', width: '100%',
    }}>
      <h2 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: C.ink, letterSpacing: -0.1 }}>{title}</h2>
      {hint && <p style={{ fontSize: 11, color: C.slate, margin: '3px 0 12px', lineHeight: 1.4 }}>{hint}</p>}
      {!hint && <div style={{ height: 10 }} />}
      {children}
    </div>
  )
}

function OpenList({ onClick, children }) {
  return (
    <button
      type="button"
      className="no-pdf"
      onClick={onClick}
      style={{
        marginTop: 10, background: '#fff', color: C.pri, border: '1px solid #b6d4de',
        borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        fontFamily: F.sans,
      }}
    >
      {children}
    </button>
  )
}

function Mini({ value, label }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px' }}>
      <b style={{ display: 'block', fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: -0.3 }}>{value}</b>
      <span style={{ fontSize: 11, color: C.slate, lineHeight: 1.3 }}>{label}</span>
    </div>
  )
}

function prettyCat(s) {
  const t = String(s || '').replace(/_/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '—'
}

function oblRag(status) {
  return status === 'overdue' ? 'red' : status === 'due_soon' ? 'amber' : 'green'
}

function ObligationCard({ item }) {
  const rag = oblRag(item.status)
  const color = ragColor(rag)
  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`,
      borderRadius: 8, padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <b style={{ fontSize: 13, color: C.ink, lineHeight: 1.35 }}>{item.name}</b>
        <Rag rag={rag} />
      </div>
      <div style={{ fontSize: 11, color: C.slate, marginTop: 6, lineHeight: 1.35 }}>
        {prettyCat(item.category)}
        {item.dueDate ? ` · due ${fmtDate(item.dueDate)}` : ''}
      </div>
    </div>
  )
}

function KpiTile({ label, value, sub, rag = 'green', onClick }) {
  const color = ragColor(rag)
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: '#fff', border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`,
        borderRadius: 8, padding: '12px 12px 11px', textAlign: 'left', width: '100%',
        cursor: onClick ? 'pointer' : 'default', fontFamily: F.sans,
      }}
    >
      <div style={{ fontSize: 11, color: C.slate, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, letterSpacing: -0.4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.slate, marginTop: 4, lineHeight: 1.35 }}>{sub}</div>}
    </button>
  )
}

function Banner({ rag, title, children }) {
  const color = ragColor(rag)
  const bg = rag === 'red' ? '#fef2f2' : '#fffbeb'
  const bd = rag === 'red' ? '#fecaca' : '#fde68a'
  return (
    <div style={{ padding: '9px 12px', background: bg, border: `1px solid ${bd}`, borderRadius: 7, margin: '10px 0' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#78350f' }}>{children}</div>
    </div>
  )
}

function BarPct({ pct, color }) {
  const n = Math.max(0, Math.min(100, Number(pct) || 0))
  return (
    <div style={{ height: 6, background: C.border, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${n}%`, background: color, borderRadius: 6 }} />
    </div>
  )
}

function SimpleTable({ columns, rows, empty = 'No rows' }) {
  if (!rows?.length) return <EmptyHint>{empty}</EmptyHint>
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={{
              textAlign: 'left', fontSize: 10, letterSpacing: 0.4, textTransform: 'uppercase',
              color: C.slateL, padding: '0 0 6px', borderBottom: `1px solid ${C.border}`,
            }}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.id || i}>
            {columns.map((c) => (
              <td key={c.key} style={{ padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                {c.render ? c.render(row) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function currentFy(d = new Date()) {
  const y = d.getFullYear()
  const mo = d.getMonth()
  return mo >= 6 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`
}

function computePackModel(data, govStats, govDash) {
  const smr = data?.smr || {}
  const investigation = data?.investigation || {}
  const patron = data?.patron || {}
  const training = data?.training || {}
  const edd = data?.edd || {}
  const venueRisk = data?.venueRisk || []
  const pack = data?.pack || {}
  const obligations = pack.obligations || {}
  const payouts = pack.payouts || {}
  const pep = pack.pep || {}

  const oblTotal = (obligations.current || 0) + (obligations.dueSoon || 0) + (obligations.overdue || 0)
  const oblPct = oblTotal > 0 ? Math.round((obligations.current / oblTotal) * 100) : 0

  const stalled = Number(investigation.stalledCases) || 0
  const stalledNoOwner = Boolean(investigation.stalledDetail && !investigation.stalledDetail.assignee)
  const invRag = stalledNoOwner ? 'red' : stalled > 0 ? 'amber' : 'green'

  const pepUnreviewed = Number(pep.unreviewed) || 0
  const pepRag = pepUnreviewed > 0 ? 'red' : (pep.recent?.length ? 'amber' : 'green')

  const sof = Number(payouts.sofOpen) || 0
  const large = (Number(payouts.eftCount) || 0) + (Number(payouts.chequeCount) || 0)
  const eftRag = sof > 0 || large > 0 ? 'amber' : 'green'

  const repeats = payouts.repeatPayees?.length || 0
  const payeeRag = repeats > 0 ? 'amber' : 'green'

  const newHr = pack.newHighRisk?.length || 0
  const hrRag = newHr > 0 ? 'amber' : 'green'

  const oblOverdue = Number(obligations.overdue) || 0
  const oblDue = Number(obligations.dueSoon) || 0
  const progRag = oblOverdue > 0 ? 'red' : oblDue > 0 ? 'amber' : 'green'

  const filed = Number(smr.filedThisQuarter) || 0
  const onTime = Number(smr.filedOnTime) || 0
  const smrPct = filed > 0 ? Math.round((onTime / filed) * 100) : 100
  const fileRag = Number(smr.overdue) > 0 ? 'red' : smrPct >= 95 ? 'green' : 'amber'

  const trainPct = Number(training.overallCompliance) || 0
  const ecddGaps = Number(patron.highRiskWithoutECDD) || 0
  const peopleRag = ecddGaps > 0 ? 'red' : trainPct > 0 && trainPct < 95 ? 'amber' : 'green'

  const processOk = [invRag, pepRag, eftRag, peopleRag].filter((r) => r === 'green').length
  const processPct = Math.round((processOk / 4) * 100)

  const eddPct = Number(edd.eddPct) || 0
  const benches = [
    smrPct >= 95,
    trainPct >= 95 || trainPct === 0,
    stalled === 0,
    eddPct >= 100 || !edd.totalStaff,
    ecddGaps === 0,
  ]
  const benchMet = benches.filter(Boolean).length
  const benchPct = Math.round((benchMet / benches.length) * 100)

  const highest = venueRisk.reduce((best, v) => {
    const s = Number(v.score) || 0
    if (!best || s > best.score) return { score: s, name: v.name, rating: v.rating || residualBand(s) }
    return best
  }, null) || { score: 0, name: '—', rating: 'Low' }

  const highRisk = (patron.riskBands?.[2]?.count || 0) + (patron.riskBands?.[3]?.count || 0)
  const riskOpen = riskOpenCount(govStats)
  const riskOverdue = Number(govStats?.riskRegister?.overdue_reviews || 0)
  const ttrOpen = ttrOpenCount(govStats)
  const ttrOverdue = Number(govStats?.ttrRegistry?.overdue_drafts || 0)
  const ttrFiled7d = Number(govStats?.ttrRegistry?.filed_last_7_days || 0)
  const enhanced = cddTierCount(govStats, 'enhanced')
  const prohibited = cddTierCount(govStats, 'prohibited')
  const standard = cddTierCount(govStats, 'standard')
  const simplified = cddTierCount(govStats, 'simplified')
  const rulesNeedReview = Number(govStats?.rules?.published_needing_review || 0)
  const rulesBackfill = Number(govStats?.rules?.with_backfill_rationale || 0)
  const checklists = govDash?.checklists || []
  const documents = govDash?.documents || []
  const reviews = govDash?.reviews || []
  const openChecklists = checklists.filter((c) => c.status === 'in_progress' || c.status === 'not_started').length
  const docsCurrent = documents.filter((d) => d.status === 'current').length
  const venueExcl = Number(patron.venueInitiatedExclusions?.total) || 0
  const venueExclQ = Number(patron.venueInitiatedExclusions?.thisQuarter) || 0
  const selfExcl = Number(patron.selfExclusions?.total) || 0
  const selfExclQ = Number(patron.selfExclusions?.thisQuarter) || 0
  const trainCompleted = Number(training.slotTotals?.completed) || (training.venues || []).reduce((s, v) => s + (v.completed || 0), 0)
  const trainOverdue = Number(training.slotTotals?.overdueStaff) || (training.venues || []).reduce((s, v) => s + (v.overdue || 0), 0)
  const opsRag = openChecklists > 0 || (reviews.length === 0) ? 'amber' : 'green'

  return {
    smr, investigation, patron, training, edd, venueRisk, pack, obligations, payouts, pep,
    oblPct, oblTotal, oblDue, oblOverdue,
    invRag, pepRag, eftRag, payeeRag, hrRag, progRag, fileRag, peopleRag, opsRag,
    stalled, pepUnreviewed, large, repeats, newHr, sof, smrPct, trainPct, ecddGaps,
    processPct, processOk, benchPct, benchMet, benchTotal: benches.length, highest,
    highRisk, riskOpen, riskOverdue, ttrOpen, ttrOverdue, ttrFiled7d,
    enhanced, prohibited, standard, simplified,
    rulesNeedReview, rulesBackfill,
    checklists, documents, reviews, openChecklists, docsCurrent,
    venueExcl, venueExclQ, selfExcl, selfExclQ,
    trainCompleted, trainOverdue,
    period: data?.period || '',
    reportDate: data?.reportDate || '',
  }
}

const LIST_TITLES = {
  filings: 'SMRs and TTRs this quarter',
  investigations: 'Open cases',
  patrons: 'High and critical patrons',
  pep: 'PEP detections',
  payments: 'Large EFT and cheques',
  payees: 'Repeat payees — 30 days',
  people: 'Overdue training',
  venues: 'Venue residual',
}

const SLIDES = [
  { id: 'c-overview', title: 'Overview' },
  { id: 'c-smr', title: 'Regulatory filings' },
  { id: 'c-inv', title: 'Investigations' },
  { id: 'c-patron', title: 'Patrons and screening' },
  { id: 'c-eft', title: 'Payouts' },
  { id: 'c-people', title: 'People' },
  { id: 'c-prog', title: 'Program' },
  { id: 'c-obl', title: 'Obligations' },
  { id: 'c-ops', title: 'Operations' },
]

const SLIDE_PARENT = {
  'c-hr': 'c-patron',
  'c-pep': 'c-patron',
  'c-excl': 'c-patron',
  'c-payee': 'c-eft',
  'c-venue': 'c-prog',
}

export default function BoardDashboardPack() {
  const [data, setData] = useState(null)
  const [govDash, setGovDash] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [listKey, setListKey] = useState(null)
  const [exporting, setExporting] = useState(null)
  const [viewMode, setViewMode] = useState('report')
  const [slideIndex, setSlideIndex] = useState(0)
  const packRef = useRef(null)
  const { data: govStats } = useGovernanceStats({ enabled: !loading && !error })

  useEffect(() => {
    const year = currentFy()
    Promise.all([
      authFetch(getLocalApiUrl('/api/governance/board-dashboard')).then((r) => r.json()),
      authFetch(getLocalApiUrl(`/api/governance/dashboard?year=${encodeURIComponent(year)}`)).then((r) => r.json()).catch(() => ({})),
    ])
      .then(([board, gov]) => {
        setData(board?.data ?? null)
        setGovDash(gov?.data ?? null)
      })
      .catch((e) => { setError(e?.message || 'Failed to load'); setData(null) })
      .finally(() => setLoading(false))
  }, [])

  const m = useMemo(() => computePackModel(data, govStats, govDash), [data, govStats, govDash])

  const goToSlide = (id) => {
    const slideId = SLIDES.some((s) => s.id === id) ? id : SLIDE_PARENT[id]
    const idx = SLIDES.findIndex((s) => s.id === slideId)
    if (idx >= 0) setSlideIndex(idx)
  }

  const jump = (id) => {
    setListKey(null)
    if (viewMode === 'preview') {
      goToSlide(id)
      return
    }
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  useEffect(() => {
    if (viewMode !== 'preview') return undefined
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault()
        setSlideIndex((i) => Math.min(SLIDES.length - 1, i + 1))
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault()
        setSlideIndex((i) => Math.max(0, i - 1))
      } else if (e.key === 'Home') {
        e.preventDefault()
        setSlideIndex(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        setSlideIndex(SLIDES.length - 1)
      } else if (e.key === 'Escape') {
        setViewMode('report')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [viewMode])

  useEffect(() => {
    if (viewMode !== 'preview') return undefined
    const t = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    return () => cancelAnimationFrame(t)
  }, [slideIndex, viewMode])

  const exportSlides = async (kind) => {
    const restore = viewMode
    setListKey(null)
    setViewMode('report')
    setExporting(kind)
    try {
      await new Promise((r) => setTimeout(r, 280))
      if (!packRef.current) throw new Error('Nothing to export')
      const slides = await captureSlides(packRef.current)
      const period = (m.period || 'board').replace(/\s+/g, '-')
      const title = `Board AML/CTF Report · ${m.period || ''}`.trim()
      const opts = { filename: `board-aml-ctf-${period}.${kind === 'pptx' ? 'pptx' : 'pdf'}`, title }
      if (kind === 'pptx') await exportSlidesToPptx(slides, opts)
      else await exportSlidesToPdf(slides, opts)
    } catch (e) {
      setError(e?.message || 'Export failed')
    } finally {
      setViewMode(restore)
      setExporting(null)
    }
  }

  const exportFormatted = async () => {
    setExporting('formatted')
    try {
      const period = (m.period || 'board').replace(/\s+/g, '-')
      await exportFormattedBoardReport(buildFormattedBoardReportData(m), {
        filename: `board-aml-ctf-formatted-${period}.pptx`,
      })
    } catch (e) {
      setError(e?.message || 'Formatted report failed')
    } finally {
      setExporting(null)
    }
  }

  const isCurrentSlide = (id) => viewMode !== 'preview' || SLIDES[slideIndex]?.id === id
  const currentSlide = SLIDES[slideIndex] || SLIDES[0]

  const packRag = packRagOf([m.invRag, m.pepRag, m.eftRag, m.payeeRag, m.hrRag, m.progRag, m.fileRag, m.peopleRag])

  const lamps = [
    { key: 'inv', title: 'Investigations', rag: m.invRag, num: m.stalled, sub: `${m.investigation.openCases || 0} open · ${m.stalled} stalled`, jump: 'c-inv' },
    { key: 'pep', title: 'PEP', rag: m.pepRag, num: m.pepUnreviewed, sub: m.pepUnreviewed ? 'Unreviewed this week' : 'None unreviewed', jump: 'c-pep' },
    { key: 'eft', title: 'EFT / cheque', rag: m.eftRag, num: m.large, sub: m.sof ? `${m.sof} SoF open` : 'Over threshold this month', jump: 'c-eft' },
    { key: 'payee', title: 'Repeat payees', rag: m.payeeRag, num: m.repeats, sub: '3+ payouts in 30 days', jump: 'c-payee' },
    { key: 'hr', title: 'New high-risk', rag: m.hrRag, num: m.newHr, sub: 'Added this month', jump: 'c-hr' },
    { key: 'prog', title: 'Program', rag: m.progRag, num: m.oblOverdue || m.oblDue, sub: m.oblOverdue ? 'Overdue' : m.oblDue ? 'Due soon' : 'Current', jump: 'c-prog' },
    { key: 'smr', title: 'Filings', rag: m.fileRag, num: `${m.smrPct}%`, sub: 'SMR on time', jump: 'c-smr' },
    { key: 'people', title: 'Patrons / training', rag: m.peopleRag, num: m.ecddGaps, sub: `ECDD gaps · training ${m.trainPct}%`, jump: 'c-patron' },
  ]

  const smrTrend = (m.pack.smr?.quarterlyTrend || []).map((r) => ({
    name: r.quarter,
    Filed: r.filed,
    'On time': r.onTime,
  }))
  const typology = (m.pack.smr?.typology || []).map((r) => ({ name: r.label, value: r.count }))
  const daysToFile = (m.pack.smr?.daysToFile || []).map((r) => ({ name: `${r.days}d`, count: r.count }))
  const stageSplit = (m.pack.cases?.stageSplit || []).map((r) => ({
    name: String(r.stage || '').replace(/_/g, ' '),
    count: r.count,
  }))
  if (m.stalled > 0 && !stageSplit.some((s) => /stall/i.test(s.name))) {
    stageSplit.push({ name: 'Stalled', count: m.stalled })
  }
  const riskMix = (m.patron.riskBands || []).map((b) => ({ name: b.label, value: b.count, color: b.color }))
  const cddMix = [
    { name: 'Simplified', value: m.simplified, color: C.green },
    { name: 'Standard', value: m.standard, color: C.priL },
    { name: 'Enhanced', value: m.enhanced, color: C.orange },
    { name: 'Prohibited', value: m.prohibited, color: C.red },
  ]
  const trainBars = (m.training.venues || []).map((v) => ({ name: v.name, pct: v.pct, target: 95 }))
  const peopleCompare = [
    { name: 'EDD', value: Number(m.edd.eddPct) || 0 },
    { name: 'Training', value: m.trainPct },
  ]
  const oblPie = [
    { name: 'Current', value: m.obligations.current || 0, color: C.green },
    { name: 'Due soon', value: m.obligations.dueSoon || 0, color: C.amber },
    { name: 'Overdue', value: m.obligations.overdue || 0, color: C.red },
  ]
  const venueBars = (m.venueRisk || []).map((v) => ({ name: v.name, score: v.score }))
  const riskReg = [
    { name: 'Open', value: m.riskOpen, color: C.amber },
    { name: 'Overdue reviews', value: m.riskOverdue, color: C.red },
  ]
  const weekly = (m.payouts.weekly || []).map((w) => ({ name: w.label, EFT: w.eft, Cheque: w.cheque }))

  const listRows = () => {
    if (listKey === 'filings') {
      return {
        columns: [
          { key: 'ref', label: 'Ref' },
          { key: 'type', label: 'Type' },
          { key: 'venue', label: 'Venue' },
          { key: 'days', label: 'Days', render: (r) => (r.days == null ? '—' : r.days) },
          { key: 'status', label: 'Status' },
        ],
        rows: m.pack.smr?.filings || [],
      }
    }
    if (listKey === 'investigations') {
      return {
        columns: [
          { key: 'caseNumber', label: 'Case' },
          { key: 'patron', label: 'Patron' },
          { key: 'status', label: 'Stage' },
          { key: 'owner', label: 'Owner', render: (r) => r.owner || 'Unassigned' },
          { key: 'idleDays', label: 'Last update', render: (r) => `${r.idleDays}d` },
        ],
        rows: m.pack.cases?.list || [],
      }
    }
    if (listKey === 'patrons') {
      return {
        columns: [
          { key: 'id', label: 'Member' },
          { key: 'name', label: 'Name' },
          { key: 'score', label: 'Score' },
          { key: 'riskLevel', label: 'Band' },
        ],
        rows: m.pack.highRiskList || [],
      }
    }
    if (listKey === 'pep') {
      return {
        columns: [
          { key: 'detectedAt', label: 'Detected', render: (r) => fmtDate(r.detectedAt) },
          { key: 'name', label: 'Patron' },
          { key: 'category', label: 'Category' },
          { key: 'unreviewed', label: '', render: (r) => <Rag rag={r.unreviewed ? 'red' : 'amber'} /> },
        ],
        rows: m.pep.recent || [],
      }
    }
    if (listKey === 'payments') {
      return {
        columns: [
          { key: 'at', label: 'Date', render: (r) => fmtDate(r.at) },
          { key: 'name', label: 'Patron' },
          { key: 'method', label: 'Method' },
          { key: 'amount', label: 'Amount', render: (r) => money(r.amount) },
          { key: 'venue', label: 'Venue' },
        ],
        rows: m.payouts.list || [],
      }
    }
    if (listKey === 'payees') {
      return {
        columns: [
          { key: 'name', label: 'Payee' },
          { key: 'times', label: 'Times' },
          { key: 'value', label: 'Value', render: (r) => money(r.value) },
          { key: 'venue', label: 'Venue' },
        ],
        rows: m.payouts.repeatPayees || [],
      }
    }
    if (listKey === 'people') {
      return {
        columns: [
          { key: 'staff', label: 'Staff' },
          { key: 'venue', label: 'Venue' },
          { key: 'role', label: 'Role' },
          { key: 'overdue', label: 'Overdue' },
        ],
        rows: m.pack.overdueTraining || [],
      }
    }
    return {
      columns: [
        { key: 'name', label: 'Venue' },
        { key: 'score', label: 'Residual' },
        { key: 'rating', label: 'Band' },
        { key: 'change', label: 'Change' },
      ],
      rows: m.venueRisk || [],
    }
  }

  return (
    <div className={`board-pack${viewMode === 'preview' ? ' is-slideshow' : ''}`} style={{ fontFamily: F.sans, color: C.ink, maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        .board-pack .pack-2, .board-pack .pack-3 { gap: 16px !important; }
        .board-pack .pack-slide .panel + .panel { margin-top: 16px; }
        .board-pack .recharts-legend-item-text { color: ${C.slate} !important; }
        .board-pack.is-slideshow .slide-stage { position: relative; overflow: hidden; }
        .board-pack.is-slideshow .pack-slide { margin-bottom: 0; }
        .board-pack.is-slideshow .pack-slide:not(.is-current) {
          position: absolute; left: -9999px; top: 0; width: 1280px; pointer-events: none;
        }
        @media (max-width: 980px) {
          .board-pack .pack-4, .board-pack .pack-3, .board-pack .pack-2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div className="no-pdf" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #b6d4de' }}>
          {[
            { id: 'report', label: 'Report' },
            { id: 'preview', label: 'Preview' },
          ].map((mode, i) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => { setListKey(null); setViewMode(mode.id) }}
              style={{
                background: viewMode === mode.id ? C.pri : '#fff',
                color: viewMode === mode.id ? '#fff' : C.pri,
                border: 0,
                borderLeft: i ? '1px solid #b6d4de' : 0,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: F.sans,
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>
        {viewMode === 'preview' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setSlideIndex((i) => Math.max(0, i - 1))}
              disabled={slideIndex === 0}
              style={{
                background: '#fff', color: C.pri, border: '1px solid #b6d4de', borderRadius: 8,
                padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: slideIndex === 0 ? 'default' : 'pointer',
                fontFamily: F.sans, opacity: slideIndex === 0 ? 0.45 : 1,
              }}
            >
              ← Prev
            </button>
            <div style={{ minWidth: 180, textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{currentSlide.title}</div>
              <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>{slideIndex + 1} / {SLIDES.length}</div>
            </div>
            <button
              type="button"
              onClick={() => setSlideIndex((i) => Math.min(SLIDES.length - 1, i + 1))}
              disabled={slideIndex === SLIDES.length - 1}
              style={{
                background: '#fff', color: C.pri, border: '1px solid #b6d4de', borderRadius: 8,
                padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: slideIndex === SLIDES.length - 1 ? 'default' : 'pointer',
                fontFamily: F.sans, opacity: slideIndex === SLIDES.length - 1 ? 0.45 : 1,
              }}
            >
              Next →
            </button>
            <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  title={s.title}
                  onClick={() => setSlideIndex(i)}
                  style={{
                    width: 8, height: 8, borderRadius: 99, padding: 0, border: 0, cursor: 'pointer',
                    background: i === slideIndex ? C.pri : '#cbd5e1',
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => exportSlides('pdf')}
            disabled={!!exporting || loading}
            style={{
              background: C.pri, color: '#fff', border: 0, borderRadius: 8,
              padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: F.sans, opacity: exporting || loading ? 0.6 : 1,
            }}
          >
            {exporting === 'pdf' ? 'Capturing slides…' : 'Export PDF'}
          </button>
          <button
            type="button"
            onClick={() => exportSlides('pptx')}
            disabled={!!exporting || loading}
            style={{
              background: '#fff', color: C.pri, border: '1px solid #b6d4de', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: F.sans, opacity: exporting || loading ? 0.6 : 1,
            }}
          >
            {exporting === 'pptx' ? 'Capturing slides…' : 'Export PowerPoint'}
          </button>
          <button
            type="button"
            onClick={exportFormatted}
            disabled={!!exporting || loading}
            style={{
              background: '#fff', color: C.pri, border: '1px solid #b6d4de', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: F.sans, opacity: exporting || loading ? 0.6 : 1,
            }}
          >
            {exporting === 'formatted' ? 'Building report…' : 'Formatted report'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: C.red, marginBottom: 12 }}>
          {error}
        </div>
      )}
      {loading && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: C.slateBg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.slate }}>
          Updating board figures…
        </div>
      )}

      <div ref={packRef} className={viewMode === 'preview' ? 'slide-stage' : undefined}>
        {listKey ? (
          <section>
            <button
              type="button"
              className="no-pdf"
              onClick={() => setListKey(null)}
              style={{ background: 'none', border: 0, color: C.pri, fontWeight: 700, fontSize: 13, padding: 0, marginBottom: 8, cursor: 'pointer', fontFamily: F.sans }}
            >
              ← Overview
            </button>
            <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 12px' }}>{LIST_TITLES[listKey]}</h1>
            <Panel title={LIST_TITLES[listKey]} hint="Live line items from tenant tables">
              <SimpleTable {...listRows()} />
            </Panel>
          </section>
        ) : (
          <section>
            <Cat id="c-overview" current={isCurrentSlide('c-overview')} rag={packRag} title="Overview" period={m.period} kicker="Board AML/CTF pack">
            <p style={{ fontSize: 12, color: C.slate, margin: '0 0 16px', lineHeight: 1.5 }}>
              Green is on track. Amber is being worked. Red needs a question at the table.
            </p>
            <div className="pack-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Obligations current', color: m.progRag === 'green' ? C.green : C.amber, sub: `${m.obligations.current || 0} of ${m.oblTotal} · ${m.oblDue} due soon`, jump: 'c-obl', donut: m.oblPct },
                { label: 'Process health', color: m.processPct >= 75 ? C.green : m.processPct >= 50 ? C.amber : C.red, sub: `${m.processOk} of 4 flows`, jump: 'c-inv', donut: m.processPct },
                { label: 'Benchmarks met', color: m.benchPct >= 80 ? C.green : C.amber, sub: `${m.benchMet} of ${m.benchTotal} targets`, jump: 'c-smr', donut: m.benchPct },
                { label: 'Highest residual', color: m.highest.score >= 70 ? C.red : m.highest.score >= 40 ? C.amber : C.green, sub: `${m.highest.name} · ${m.highest.rating}`, jump: 'c-venue', donut: m.highest.score, suffix: '' },
              ].map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => jump(s.jump)}
                  style={{
                    background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: '14px 14px', display: 'flex', gap: 12, alignItems: 'center',
                    cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: F.sans,
                  }}
                >
                  <Donut value={s.donut} color={s.color} suffix={s.suffix} />
                  <div>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.25 }}>{s.label}</span>
                    <small style={{ display: 'block', fontSize: 11, color: C.slate, marginTop: 4, lineHeight: 1.35 }}>{s.sub}</small>
                  </div>
                </button>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, margin: '0 0 10px' }}>
              Where to look first
            </div>
            <div className="pack-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {lamps.map((lamp) => (
                <button
                  key={lamp.key}
                  type="button"
                  onClick={() => jump(lamp.jump)}
                  style={{
                    background: '#fff', border: `1px solid ${C.border}`, borderLeft: `4px solid ${ragColor(lamp.rag)}`,
                    borderRadius: 8, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: F.sans,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <h3 style={{ fontSize: 12, fontWeight: 600, margin: 0, color: C.slate }}>{lamp.title}</h3>
                    <Rag rag={lamp.rag} />
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, letterSpacing: -0.4, color: ragColor(lamp.rag) }}>{lamp.num}</div>
                  <p style={{ fontSize: 11, color: C.slate, margin: '6px 0 0', lineHeight: 1.35 }}>{lamp.sub}</p>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, margin: '0 0 10px' }}>
              Key indicators
            </div>
            <div className="pack-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              <KpiTile label="Open cases" value={m.investigation.openCases || 0} sub={m.investigation.avgAgeOpenDays ? `Avg age ${m.investigation.avgAgeOpenDays}d` : 'No open cases'} rag={m.invRag} onClick={() => jump('c-inv')} />
              <KpiTile label="High-risk patrons" value={m.highRisk} sub={m.ecddGaps ? `${m.ecddGaps} without ECDD` : 'ECDD current'} rag={m.ecddGaps > 0 ? 'amber' : 'green'} onClick={() => jump('c-hr')} />
              <KpiTile label="Venue exclusions" value={m.venueExcl} sub={`${m.venueExclQ} this quarter`} rag="green" onClick={() => jump('c-excl')} />
              <KpiTile label="EDD completion" value={m.edd.totalStaff ? `${m.edd.eddPct}%` : '—'} sub={m.edd.totalStaff ? `${m.edd.eddCompleted} of ${m.edd.totalStaff}` : 'Not reported'} rag={m.edd.overdue > 0 ? 'amber' : 'green'} onClick={() => jump('c-people')} />
              <KpiTile label="Training" value={`${m.trainPct}%`} sub={`${m.trainCompleted} done · ${m.trainOverdue} overdue`} rag={m.trainPct >= 95 || m.trainPct === 0 ? 'green' : 'amber'} onClick={() => jump('c-people')} />
              <KpiTile label="TTR to file" value={m.ttrOpen} sub={m.ttrOverdue > 0 ? `${m.ttrOverdue} overdue` : `${m.ttrFiled7d} filed last 7d`} rag={m.ttrOverdue > 0 ? 'red' : m.ttrOpen > 0 ? 'amber' : 'green'} onClick={() => jump('c-smr')} />
              <KpiTile label="Rules unreviewed" value={m.rulesNeedReview} sub={m.rulesBackfill > 0 ? `${m.rulesBackfill} on backfill rationale` : 'All published rules reviewed'} rag={m.rulesNeedReview > 0 ? 'amber' : 'green'} onClick={() => jump('c-ops')} />
              <KpiTile label="Open checklists" value={m.openChecklists} sub={`${m.docsCurrent}/${(m.documents || []).length} docs current`} rag={m.openChecklists > 0 ? 'amber' : 'green'} onClick={() => jump('c-ops')} />
            </div>
            </Cat>

            <Cat id="c-smr" current={isCurrentSlide('c-smr')} rag={m.fileRag} title="Regulatory filings" period={m.period}>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <Panel title="SMR filings — four quarters" hint={m.smr.filedThisQuarter ? `${m.smrPct}% on time this quarter` : 'No SMRs filed this quarter'}>
                  <ChartBox empty={!smrTrend.length}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={smrTrend}>
                        <ChartGrid />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TIP} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={LEG} />
                        <Bar dataKey="Filed" fill={C.pri} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="On time" fill={C.green} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                </Panel>
                <Panel title="SMRs this quarter by typology" hint="Activity mix — not a target">
                  <ChartBox height={220} empty={!typology.length}>
                    <CentrePie
                      data={typology.map((r, i) => ({ ...r, color: [C.pri, C.amber, C.orange, C.purple, C.green][i % 5] }))}
                      height={220}
                    />
                  </ChartBox>
                </Panel>
              </div>
              <Panel title="Suspicion to file — days" hint="Target ≤3 business days">
                <ChartBox height={168} empty={!daysToFile.length}>
                  <ResponsiveContainer width="100%" height={168}>
                    <LineChart data={daysToFile}>
                      <ChartGrid />
                      <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={TIP} />
                      <Line type="monotone" dataKey="count" stroke={C.pri} strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartBox>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                  <Mini value={m.smr.filedThisQuarter || 0} label="Filed this Q" />
                  <Mini value={m.smr.filedLate || 0} label="Filed late" />
                  <Mini value={m.smr.filedThisQuarter ? (m.smr.avgDaysToFile ?? '—') : '—'} label="Avg days to file" />
                  <Mini value={m.ttrOpen} label="TTR open" />
                </div>
                <OpenList onClick={() => setListKey('filings')}>Open filing list →</OpenList>
              </Panel>
            </Cat>

            <Cat id="c-inv" current={isCurrentSlide('c-inv')} rag={m.invRag} title="Investigations" period={m.period}>
              <Panel title="Investigation pipeline" hint={m.investigation.stalledDetail ? `${m.investigation.stalledDetail.id || 'Stalled case'} · ${m.investigation.stalledDetail.stageDays || 0} days` : 'Open cases by stage'}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10 }}>
                  <Mini value={m.investigation.openCases || 0} label="Open cases" />
                  <Mini value={m.investigation.openCases ? (m.investigation.avgAgeOpenDays ?? '—') : '—'} label="Avg age (days)" />
                  <Mini value={m.investigation.closedThisQuarter || 0} label="Closed this Q" />
                  <Mini value={m.investigation.escalatedToSMR || 0} label="Escalated to SMR" />
                </div>
                {m.investigation.stalledDetail && (
                  <Banner rag={m.invRag} title={`Stalled — ${m.investigation.stalledDetail.id || 'case'}`}>
                    {m.investigation.stalledDetail.patron ? `Patron ${m.investigation.stalledDetail.patron} · ` : ''}
                    {m.investigation.stalledDetail.stage}
                    {m.investigation.stalledDetail.stageDays ? ` · ${m.investigation.stalledDetail.stageDays} days since last update` : ''}
                    {m.investigation.stalledDetail.assignee ? ` · Assigned: ${m.investigation.stalledDetail.assignee}` : ' · Unassigned'}
                  </Banner>
                )}
                <ChartBox height={168} empty={!stageSplit.length}>
                  <ResponsiveContainer width="100%" height={168}>
                    <BarChart data={stageSplit}>
                      <ChartGrid />
                      <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={TIP} />
                      <Bar dataKey="count" fill={m.invRag === 'red' ? C.red : C.pri} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBox>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                  <Mini value={m.investigation.avgDaysToClose || '—'} label="Avg days to close" />
                  <Mini value={m.investigation.dismissedNoAction || 0} label="Dismissed, no action" />
                </div>
                <OpenList onClick={() => setListKey('investigations')}>Open case list →</OpenList>
              </Panel>
            </Cat>

            <Cat id="c-patron" current={isCurrentSlide('c-patron')} rag={m.peopleRag} title="Patrons and screening" extra={<Rag rag={m.pepRag} />} period={m.period}>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <Panel title="Patron risk mix" hint={`Watchlist ${m.patron.watchlistTotal || 0} · ECDD gaps ${m.ecddGaps} · ${m.highRisk} high / critical`}>
                  <ChartBox height={220} empty={!riskMix.some((x) => x.value)}>
                    <CentrePie data={riskMix} height={220} centerValue={m.highRisk} centerLabel="High-risk" />
                  </ChartBox>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                    <Mini value={(m.patron.totalActive || 0).toLocaleString()} label="Active patrons" />
                    <Mini value={m.patron.watchlistTotal || 0} label="Watchlist" />
                    <Mini value={m.highRisk} label="High / critical" />
                    <Mini value={m.ecddGaps} label="Without ECDD" />
                  </div>
                </Panel>
                <Panel title="CDD tier mix" hint={`Enhanced ${m.enhanced} · prohibited ${m.prohibited}`}>
                  <ChartBox height={220} empty={!cddMix.some((x) => x.value)}>
                    <CentrePie data={cddMix} height={220} centerValue={m.enhanced} centerLabel="Enhanced" />
                  </ChartBox>
                </Panel>
              </div>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <Panel id="c-hr" title="New high-risk patrons" hint="Added this month">
                  <SimpleTable
                    columns={[
                      { key: 'addedAt', label: 'Added', render: (r) => fmtDate(r.addedAt) },
                      { key: 'name', label: 'Patron' },
                      { key: 'score', label: 'Score' },
                    ]}
                    rows={(m.pack.newHighRisk || []).slice(0, 6)}
                    empty="None added this month"
                  />
                  <OpenList onClick={() => setListKey('patrons')}>Open high-risk list →</OpenList>
                </Panel>
                <Panel id="c-pep" title="Latest PEP detections" hint="Red = detected in the last 7 days">
                  <SimpleTable
                    columns={[
                      { key: 'detectedAt', label: 'Detected', render: (r) => fmtDate(r.detectedAt) },
                      { key: 'name', label: 'Patron' },
                      { key: 'unreviewed', label: '', render: (r) => <Rag rag={r.unreviewed ? 'red' : 'amber'} /> },
                    ]}
                    rows={(m.pep.recent || []).slice(0, 6)}
                    empty="No PEP detections"
                  />
                  <OpenList onClick={() => setListKey('pep')}>Open PEP list →</OpenList>
                </Panel>
              </div>
              <div id="c-excl" className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
                <Panel title="Exclusions" hint="Venue-initiated and self-exclusions">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <Mini value={m.venueExcl} label={`Venue-initiated · ${m.venueExclQ} this Q`} />
                    <Mini value={m.selfExcl} label={`Self-exclusions · ${m.selfExclQ} this Q`} />
                  </div>
                  <SimpleTable
                    columns={[
                      { key: 'patron', label: 'Patron' },
                      { key: 'venue', label: 'Venue' },
                      { key: 'date', label: 'Date' },
                      { key: 'reason', label: 'Reason' },
                    ]}
                    rows={m.patron.venueInitiatedExclusions?.list || []}
                    empty="No venue-initiated exclusions listed"
                  />
                </Panel>
                <Panel title="Risk distribution" hint="Same bands as Dashboard 1">
                  {(m.patron.riskBands || []).map((b) => (
                    <div key={b.label} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
                        <span>{b.label}</span>
                        <span style={{ fontWeight: 700, color: b.color }}>{(b.count || 0).toLocaleString()} ({b.pct || 0}%)</span>
                      </div>
                      <BarPct pct={b.pct} color={b.color} />
                    </div>
                  ))}
                </Panel>
              </div>
            </Cat>

            <Cat id="c-eft" current={isCurrentSlide('c-eft')} rag={m.eftRag} title="Payouts" period={m.period}>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <Panel title="Large EFT and cheques — this month" hint={`Weekly count over $${(m.payouts.threshold || 5000).toLocaleString()} · ${m.sof} SoF open`}>
                  <ChartBox empty={!weekly.length || !weekly.some((w) => w.EFT || w.Cheque)}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={weekly}>
                        <ChartGrid />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TIP} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={LEG} />
                        <Bar dataKey="EFT" fill={C.pri} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Cheque" fill={C.amber} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginTop: 10 }}>
                    <Mini value={m.payouts.eftCount || 0} label="EFT" />
                    <Mini value={m.payouts.chequeCount || 0} label="Cheques" />
                    <Mini value={money(m.payouts.totalValue)} label="Value" />
                    <Mini value={m.sof} label="SoF open" />
                  </div>
                  <OpenList onClick={() => setListKey('payments')}>Open EFT / cheque list →</OpenList>
                </Panel>
                <Panel id="c-payee" title="Repeat payees — 30 days" hint="3+ payouts to the same payee">
                  <SimpleTable
                    columns={[
                      { key: 'name', label: 'Payee' },
                      { key: 'times', label: 'Times' },
                      { key: 'value', label: 'Value', render: (r) => money(r.value) },
                    ]}
                    rows={(m.payouts.repeatPayees || []).slice(0, 8)}
                    empty="No repeat payees"
                  />
                  <OpenList onClick={() => setListKey('payees')}>Open repeat payees →</OpenList>
                </Panel>
              </div>
            </Cat>

            <Cat id="c-people" current={isCurrentSlide('c-people')} rag={m.peopleRag} title="People — training and EDD" period={m.period}>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <Panel title="Training by venue" hint={`${m.trainCompleted} completed · ${m.trainOverdue} overdue · vs 95% target`}>
                  <ChartBox height={168} empty={!trainBars.length}>
                    <ResponsiveContainer width="100%" height={168}>
                      <BarChart data={trainBars}>
                        <ChartGrid />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TIP} />
                        <Bar dataKey="pct" fill={C.green} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="target" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                  <OpenList onClick={() => setListKey('people')}>Open overdue staff →</OpenList>
                </Panel>
                <Panel title="EDD vs training" hint={`EDD ${m.edd.eddPct || 0}% · training ${m.trainPct}%`}>
                  <ChartBox height={168} empty={!m.edd.totalStaff && !m.trainPct}>
                    <ResponsiveContainer width="100%" height={168}>
                      <BarChart data={peopleCompare}>
                        <ChartGrid />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TIP} />
                        <Bar dataKey="value" fill={C.pri} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                  {m.edd.overdue > 0 && (
                    <Banner rag="amber" title={`${m.edd.overdue} staff with overdue EDD`}>
                      {m.edd.expiringSoon > 0 ? `${m.edd.expiringSoon} expiring soon.` : 'Compliance team notified.'}
                    </Banner>
                  )}
                </Panel>
              </div>
              <div className="pack-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
                <Panel title="Overdue training by role" hint={m.training.nextDueWithin30Days > 0 ? `${m.training.nextDueWithin30Days} renewals due within 30 days` : 'Role roll-up'}>
                  <SimpleTable
                    columns={[
                      { key: 'role', label: 'Role' },
                      { key: 'total', label: 'Required' },
                      { key: 'overdue', label: 'Overdue', render: (r) => (r.overdue > 0 ? `${r.overdue} overdue` : 'All current') },
                    ]}
                    rows={m.training.overdueBreakdown || []}
                    empty="No role breakdown"
                  />
                </Panel>
                <Panel title="EDD by role" hint={m.edd.totalStaff ? `${m.edd.eddCompleted} of ${m.edd.totalStaff} screened` : 'No EDD rows'}>
                  <SimpleTable
                    columns={[
                      { key: 'role', label: 'Role' },
                      { key: 'completed', label: 'Screened', render: (r) => `${r.completed}/${r.total}` },
                      { key: 'overdue', label: 'Overdue', render: (r) => (r.overdue > 0 ? `${r.overdue} overdue` : 'Current') },
                    ]}
                    rows={m.edd.byRole || []}
                    empty="No role-level EDD breakdown"
                  />
                </Panel>
              </div>
            </Cat>

            <Cat id="c-prog" current={isCurrentSlide('c-prog')} rag={m.progRag} title="Program, venue and risk register" period={m.period}>
              <div className="pack-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                <Panel title="Program obligation status" hint="Current / due soon / overdue">
                  <ChartBox height={200} empty={!oblPie.some((x) => x.value)}>
                    <CentrePie data={oblPie} height={200} centerValue={`${m.oblPct}%`} centerLabel="Current" />
                  </ChartBox>
                </Panel>
                <Panel id="c-venue" title="Venue residual" hint={`${m.highest.name} ${m.highest.score} · appetite: no High`}>
                  <ChartBox height={168} empty={!venueBars.length}>
                    <ResponsiveContainer width="100%" height={168}>
                      <BarChart data={venueBars}>
                        <ChartGrid />
                        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TIP} />
                        <Bar dataKey="score" fill={C.amber} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                  <OpenList onClick={() => setListKey('venues')}>Open venue assessments →</OpenList>
                </Panel>
                <Panel title="Risk register status" hint={`${m.riskOpen} open · ${m.riskOverdue} overdue reviews · TTR open ${m.ttrOpen}`}>
                  <ChartBox height={200} empty={!riskReg.some((x) => x.value)}>
                    <CentrePie data={riskReg} height={200} centerValue={m.riskOpen} centerLabel="Open" />
                  </ChartBox>
                </Panel>
              </div>
              <div style={{ marginTop: 20 }}>
                <Panel title="Venue risk ratings" hint="Score / 100 · flags from the risk register">
                  {(m.venueRisk || []).length === 0 ? (
                    <EmptyHint>No venue risk rows yet</EmptyHint>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      {m.venueRisk.map((v) => {
                        const color = v.rating === 'High' ? C.red : v.rating === 'Medium' ? C.amber : C.green
                        return (
                          <div key={v.id || v.name} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <b style={{ fontSize: 13 }}>{v.name}</b>
                              <span style={{ fontSize: 11, fontWeight: 800, color }}>{v.rating}</span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 900, color, margin: '4px 0' }}>{v.score}<span style={{ fontSize: 11, color: C.slateL, fontWeight: 600 }}> /100</span></div>
                            <div style={{ fontSize: 11, color: C.slate }}>{v.change === 'up' ? `+${(v.score || 0) - (v.prev || 0)}` : v.change === 'down' ? `-${(v.prev || 0) - (v.score || 0)}` : 'No change'} vs prior</div>
                            {(v.flags || []).slice(0, 2).map((f) => (
                              <div key={f} style={{ fontSize: 11, color: '#78350f', marginTop: 3 }}>{f}</div>
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Panel>
              </div>
            </Cat>

            <Cat id="c-obl" current={isCurrentSlide('c-obl')} rag={m.progRag} title="Program obligations" period={m.period}>
              <p style={{ fontSize: 12, color: C.slate, margin: '0 0 16px', lineHeight: 1.5 }}>
                Same register as the Program donut, split so the board works red and amber first.
              </p>
              <div className="pack-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                {[
                  { title: 'Ask questions', hint: 'Overdue', status: 'overdue' },
                  { title: 'Working on it', hint: 'Due soon', status: 'due_soon' },
                  { title: 'OK', hint: 'Current', status: 'current' },
                ].map((col) => {
                  const items = (m.obligations.items || []).filter((o) => o.status === col.status)
                  return (
                    <Panel
                      key={col.status}
                      title={col.title}
                      hint={`${items.length} ${col.hint.toLowerCase()}`}
                    >
                      {items.length === 0 ? (
                        <EmptyHint>None {col.hint.toLowerCase()}</EmptyHint>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {items.map((item) => (
                            <ObligationCard key={item.name} item={item} />
                          ))}
                        </div>
                      )}
                    </Panel>
                  )
                })}
              </div>
            </Cat>

            <Cat id="c-ops" current={isCurrentSlide('c-ops')} rag={m.opsRag} title="Operations — checklists, documents, review" period={m.period}>
              <div className="pack-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                <Panel title="Active checklists" hint={`${m.openChecklists} open`}>
                  <SimpleTable
                    columns={[
                      { key: 'name', label: 'Checklist' },
                      { key: 'progress', label: 'Progress', render: (r) => `${r.progress || 0}/${r.total || 0}` },
                      { key: 'status', label: 'Status' },
                    ]}
                    rows={(m.checklists || []).slice(0, 6)}
                    empty="No checklists yet"
                  />
                </Panel>
                <Panel title="AML/CTF program documents" hint={`${m.docsCurrent} current`}>
                  <SimpleTable
                    columns={[
                      { key: 'name', label: 'Document' },
                      { key: 'version', label: 'Ver' },
                      { key: 'status', label: 'Status' },
                    ]}
                    rows={(m.documents || []).slice(0, 6)}
                    empty="No program documents"
                  />
                </Panel>
                <Panel title="Independent reviews" hint="External review outcomes">
                  <SimpleTable
                    columns={[
                      { key: 'period', label: 'Period' },
                      { key: 'reviewerOrg', label: 'Reviewer' },
                      { key: 'status', label: 'Status' },
                    ]}
                    rows={(m.reviews || []).slice(0, 6)}
                    empty="No independent reviews"
                  />
                </Panel>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, margin: '12px 0' }}>
                <Mini value={m.rulesNeedReview} label="Rules needing review" />
                <Mini value={m.rulesBackfill} label="Backfill rationale" />
                <Mini value={m.riskOpen} label="Risk register open" />
                <Mini value={m.riskOverdue} label="Overdue risk reviews" />
              </div>
            </Cat>
          </section>
        )}
      </div>
    </div>
  )
}

function Cat({ id, rag, title, extra, period, kicker, children, current = true }) {
  return (
    <div
      id={id}
      data-slide={title}
      className={`pack-slide${current ? ' is-current' : ''}`}
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '20px 22px 18px',
        marginBottom: 24,
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 16, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${C.border}`,
      }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase',
            color: C.pri, marginBottom: 4,
          }}>
            {kicker || 'Board AML/CTF pack'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.4, color: C.ink }}>{title}</h2>
            <Rag rag={rag} />
            {extra}
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.slate, textAlign: 'right', flexShrink: 0, lineHeight: 1.45 }}>
          <div style={{ fontWeight: 700, color: C.pri }}>{period || 'Board pack'}</div>
          Confidential — Board
        </div>
      </div>
      {children}
    </div>
  )
}
